import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { StrategistStandardsMatrixResponse } from '@/entities/strategist';
import { HeatmapLegend, HeatmapMatrix, RELIABLE_MIN_DIALOGS } from '@/widgets/StrategistHeatmap';
import type { HeatmapAxisItem, HeatmapCell } from '@/widgets/StrategistHeatmap';
import { StandardsKpis } from './StandardsKpis';
import type { BestPair } from './StandardsKpis';

type Props = {
  /** Данные матрицы стандартов (непустые: есть строки и столбцы) */
  data: StrategistStandardsMatrixResponse;
  /** Открыть карточку понятия по conceptId (только строки; столбцы — блоки стандарта) */
  onConceptOpen: (conceptId: string) => void;
  /** Проваливание в диалоги ячейки (понятие × блок стандарта) с готовым заголовком */
  onCellOpen: (params: { objectionId: string; block: string; title: string }) => void;
};

/**
 * Визуализация матрицы «понятие × блок стандарта»: KPI-строка, легенда шкалы и
 * тепловая матрица. Ожидает непустые данные — пустое состояние обрабатывает страница.
 */
export const StrategistStandardsViz: FC<Props> = ({ data, onConceptOpen, onCellOpen }) => {
  const { t } = useTranslation('strategist');

  // Столбцы-блоки адресуются кодом (`code`) — приводим к обобщённой оси (id/label).
  const columns = useMemo<HeatmapAxisItem[]>(
    () => data.columns.map((c) => ({ id: c.code, label: c.label })),
    [data.columns],
  );

  // Подписи строк-понятий и столбцов-блоков — для заголовка дровера диалогов ячейки.
  const rowLabelById = useMemo(() => new Map(data.rows.map((r) => [r.id, r.label ?? '—'])), [data.rows]);
  const blockLabelByCode = useMemo(() => new Map(data.columns.map((c) => [c.code, c.label ?? '—'])), [data.columns]);

  // Ячейки: строка = conceptId, столбец = block (код блока).
  const heatmapCells = useMemo<HeatmapCell[]>(
    () =>
      data.cells.map((c) => ({
        rowId: c.conceptId,
        colId: c.block,
        nDialogs: c.nDialogs,
        targetRate: c.targetRate,
      })),
    [data.cells],
  );

  // Лучшая устойчивая пара: максимальная доля цели среди надёжных ячеек.
  const bestPair = useMemo<BestPair | null>(() => {
    const rowsById = new Map(data.rows.map((r) => [r.id, r.label ?? '—']));
    const columnsByCode = new Map(data.columns.map((c) => [c.code, c.label ?? '—']));
    let best: BestPair | null = null;
    data.cells.forEach((cell) => {
      if (cell.targetRate === null || cell.nDialogs < RELIABLE_MIN_DIALOGS) return;
      if (best === null || cell.targetRate > best.rate) {
        best = {
          conceptLabel: rowsById.get(cell.conceptId) ?? '—',
          blockLabel: columnsByCode.get(cell.block) ?? '—',
          rate: cell.targetRate,
        };
      }
    });
    return best;
  }, [data]);

  return (
    <div className="flex flex-col gap-4">
      <StandardsKpis totalDialogs={data.totalDialogs} baselineRate={data.baselineRate} bestPair={bestPair} />
      <HeatmapLegend
        labels={{
          belowBaseline: t('legend.belowBaseline'),
          nearBaseline: t('legend.nearBaseline'),
          aboveBaseline: t('legend.aboveBaseline'),
          unreliable: (minDialogs) => t('legend.unreliable', { n: minDialogs }),
        }}
      />
      <HeatmapMatrix
        rows={data.rows}
        columns={columns}
        cells={heatmapCells}
        baselineRate={data.baselineRate}
        onRowClick={onConceptOpen}
        onCellClick={(rowId, colId) =>
          onCellOpen({
            objectionId: rowId,
            block: colId,
            title: `${rowLabelById.get(rowId) ?? '—'} → ${blockLabelByCode.get(colId) ?? '—'}`,
          })
        }
        labels={{
          corner: t('standards.corner'),
          total: t('matrix.total'),
          rowName: t('standards.concept'),
          colName: t('standards.block'),
          dialogs: t('matrix.dialogs'),
          targetRate: t('matrix.targetRate'),
          unreliableHint: t('matrix.unreliableHint'),
        }}
      />
    </div>
  );
};
